import * as Clipboard from 'expo-clipboard';
import { Platform, View } from 'react-native';
import Toast from 'react-native-toast-message';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

import { useSendSignLink } from '@/api/partners/reservations/use-send-sign-link';
import { useSignLink } from '@/api/partners/reservations/use-sign-link';
import { Button, Card, showErrorMessage, Text } from '@/ui';

type Props = {
  reservationId: number;
};

// Shown on pending EonOne reservations: the customer still has to sign the
// contract and pay, so the admin can email them the sign-and-pay link or
// copy it to share manually.
export const PendingActions = ({ reservationId }: Props) => {
  const { styles } = useStyles(stylesheet);

  const {
    mutate: getSignLink,
    mutateAsync: getSignLinkAsync,
    isPending: isCopying,
  } = useSignLink();
  const { mutate: sendSignLink, isPending: isSending } = useSendSignLink();

  const showCopiedToast = () => {
    Toast.show({
      type: 'success',
      text1: 'Link copied to clipboard',
      visibilityTime: 4000,
    });
  };

  const handleCopyLink = async () => {
    // Safari only allows clipboard writes during the user gesture that
    // triggered them. Awaiting the network request before writing (as the
    // native flow below does) loses that gesture context, so Safari rejects
    // the write while Chrome allows it. The ClipboardItem API lets us hand the
    // browser a promise synchronously inside the click handler, so the write is
    // still attributed to the gesture once the URL resolves.
    if (
      Platform.OS === 'web' &&
      typeof ClipboardItem !== 'undefined' &&
      navigator.clipboard?.write
    ) {
      try {
        await navigator.clipboard.write([
          new ClipboardItem({
            'text/plain': getSignLinkAsync({ reservationId }).then(
              ({ url }) => new Blob([url], { type: 'text/plain' })
            ),
          }),
        ]);

        showCopiedToast();
      } catch {
        showErrorMessage('Failed to copy the sign link');
      }
      return;
    }

    getSignLink(
      { reservationId },
      {
        onSuccess: async ({ url }) => {
          const copied = await Clipboard.setStringAsync(url);

          if (!copied) {
            showErrorMessage('Issue copying to clipboard');
            return;
          }

          showCopiedToast();
        },
        onError: () => {
          showErrorMessage('Failed to generate the sign link');
        },
      }
    );
  };

  const handleSendEmail = () => {
    sendSignLink(
      { reservationId },
      {
        onSuccess: () => {
          Toast.show({
            type: 'success',
            text1: 'Sign & pay link emailed to the customer',
            visibilityTime: 4000,
          });
        },
        onError: () => {
          showErrorMessage('Failed to send the email');
        },
      }
    );
  };

  return (
    <Card>
      <View style={styles.container}>
        <View style={{ flexShrink: 1, gap: 4 }}>
          <Text type="bodyBold">Awaiting customer signature & payment</Text>

          <Text type="caption1" style={styles.secondaryText}>
            Share the sign & pay link so the customer can confirm this
            reservation.
          </Text>
        </View>

        <View style={styles.buttons}>
          <Button
            text="Copy link"
            type="secondarySmall"
            loading={isCopying}
            onPress={handleCopyLink}
            style={{ width: 'auto' }}
          />

          <Button
            text="Send email"
            type="primarySmall"
            loading={isSending}
            onPress={handleSendEmail}
            style={{ width: 'auto' }}
          />
        </View>
      </View>
    </Card>
  );
};

const stylesheet = createStyleSheet((theme) => ({
  container: {
    padding: 16,
    gap: 16,
    flexDirection: { xs: 'column', md: 'row' },
    alignItems: { xs: 'stretch', md: 'center' },
    justifyContent: 'space-between',
  },
  buttons: {
    flexDirection: 'row',
    gap: 8,
  },
  secondaryText: {
    color: theme.colors.text.secondary,
  },
}));
